import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Camera, MapPin, FileText, Truck, ShieldCheck, Scan } from "@phosphor-icons/react";
import DemoVideo from "@/components/DemoVideo";
import PlanCards, { usePlans } from "@/components/PlanCards";

const SECOND = "https://images.unsplash.com/photo-1627666260660-812e4684a600?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHx0cnVjayUyMGRyaXZlciUyMHRhYmxldHxlbnwwfHx8fDE3OTI4NDU5NDZ8MA&ixlib=rb-4.1.0&q=75&w=1200";
const LOT = "https://images.unsplash.com/photo-1770319942638-a5989632f2ad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxjYXIlMjBsb3QlMjBkZWFsZXJzaGlwfGVufDB8fHx8MTc5Mjg0NTk0Nnww&ixlib=rb-4.1.0&q=75&w=1200";

function Pricing({ onStart }) {
  const data = usePlans();
  if (!data?.plans?.length) return null;
  return <section id="pricing" className="py-20 lg:py-28 scroll-mt-4" data-testid="pricing-section">
    <div className="max-w-7xl mx-auto px-4 lg:px-6">
      <div className="eyebrow text-accent mb-3">Pricing</div>
      <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">One price per fleet size.</h2>
      <p className="text-muted-foreground mt-3 mb-10 max-w-xl">Every plan includes every feature. Start with a {data.trial_days}-day free trial, no card needed.</p>
      <PlanCards plans={data.plans} action={(p) =>
        <button onClick={onStart} data-testid={`pricing-start-${p.id}`} className="btn-amber w-full">Start free trial</button>}/>
    </div>
  </section>;
}


export default function Marketing() {
  const nav = useNavigate();
  return (
    <div className="bg-background text-foreground">
      {/* top strip */}
      <div className="bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 lg:px-6 h-12">
          <Link to="/" className="flex items-center gap-2 font-display font-black tracking-tight text-lg">
            <span className="inline-block w-2 h-6 bg-accent" aria-hidden />
            HAULGUARD
          </Link>
          <div className="flex items-center gap-3">
            <a href="#pricing" data-testid="nav-pricing" className="hidden sm:inline text-xs font-bold tracking-wider uppercase hover:text-accent">Pricing</a>
            <Link to="/login" data-testid="nav-login" className="text-xs font-bold tracking-wider uppercase hover:text-accent">Sign in</Link>
            <Link to="/register" data-testid="nav-signup" className="btn-amber !py-2 !px-3 !text-xs">Start free</Link>
          </div>
        </div>
      </div>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <picture>
          <source media="(max-width: 767px)" srcSet="/images/hero-mobile.jpg"/>
          <img src="/images/hero-desktop.jpg" alt="Nine-car transporter loaded at a dealership lot at golden hour" data-testid="hero-image" fetchpriority="high"
               className="absolute inset-0 w-full h-full object-cover object-top md:object-center" />
        </picture>
        <div className="absolute inset-0 hero-overlay-mobile md:hidden" />
        <div className="absolute inset-0 hero-overlay hidden md:block" />
        <div className="relative max-w-7xl mx-auto px-4 lg:px-6 pt-[58vh] pb-12 md:py-24 lg:py-40">
          <div className="max-w-2xl text-white">
            <div className="eyebrow text-accent mb-5">Built for the car haul</div>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
              Every vehicle.<br/>
              <span className="text-accent">Accounted for.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg leading-relaxed max-w-xl text-white/90">
              Vehicle-by-vehicle condition records and location intelligence built for professional car haulers.
              Keep VIN details, pickup and delivery inspections, evidence, and repeat-location notes together — from the first scan to the final handoff.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={() => nav("/register")} data-testid="cta-primary" className="btn-amber">
                Start your first vehicle record <ArrowRight size={18} weight="bold"/>
              </button>
              <a href="#demo" data-testid="cta-secondary" className="btn-ghost-dark">See how it works</a>
            </div>
            <div className="mt-10 flex items-center gap-4 text-xs text-white/70 font-mono-tech">
              <span>VIN SCAN</span><span className="w-6 h-px bg-accent"/>
              <span>PICKUP</span><span className="w-6 h-px bg-accent"/>
              <span>DELIVERY</span><span className="w-6 h-px bg-accent"/>
              <span>PDF HANDOFF</span>
            </div>
          </div>
        </div>
        <div className="relative amber-stripe h-1"/>
      </section>

      <DemoVideo/>

      {/* THE VEHICLE IS THE RECORD */}
      <section className="relative py-20 lg:py-28 grid-pattern">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="eyebrow text-accent mb-4">01 · The Vehicle Is the Record</div>
            <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">A truckload is a route.<br/>A claim is <span className="text-accent">one vehicle</span>.</h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Photos, damage notes, signatures and timestamps attach to the VIN — not the load.
              So when a dealer calls about a scratch on stock #L204, you pull up <em>that</em> car,
              not forty of them.
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 gap-4">
            {[
              { k: "VIN-first", v: "Scan or OCR, prefill from NHTSA.", icon: Scan },
              { k: "Dual handoff", v: "Pickup + delivery, step-by-step.", icon: Camera },
              { k: "Signatures", v: "Collected on the glass, in seconds.", icon: ShieldCheck },
              { k: "Claims-ready", v: "One tap to a factual PDF.", icon: FileText },
            ].map((c,i) => (
              <div key={i} className="corner-bracket relative bg-white border border-border/60 p-5">
                <c.icon size={28} weight="duotone" className="text-primary"/>
                <div className="mt-4 font-display font-bold text-lg">{c.k}</div>
                <div className="text-sm text-muted-foreground">{c.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DOCUMENT BOTH HANDOFFS */}
      <section id="how" className="py-20 lg:py-28 bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="relative">
              <img src={SECOND} alt="Driver tablet inspection" loading="lazy" decoding="async" className="w-full h-80 lg:h-[480px] object-cover border-4 border-accent/60"/>
              <div className="absolute -top-3 -left-3 bg-accent text-accent-foreground px-3 py-1 font-mono-tech text-[11px] font-bold">PICKUP · 06:42 CT</div>
              <div className="absolute -bottom-3 -right-3 bg-white text-primary px-3 py-1 font-mono-tech text-[11px] font-bold">DELIVERY · 18:08 CT</div>
            </div>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="eyebrow text-accent mb-4">02 · Document Both Handoffs</div>
            <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">From the first photo<br/>to the receiver's signature.</h2>
            <p className="mt-5 text-white/80 leading-relaxed">
              A guided inspection prompts you through the shots that matter — front, sides, rear, VIN, odometer, damage close-ups — then does it again at delivery so comparison is simple.
            </p>
            <ul className="mt-6 space-y-3 text-sm">
              {["Required shot checklist", "Damage markers by vehicle area", "GPS + timestamp on every capture", "On-glass signature with printed name"].map((x,i) => (
                <li key={i} className="flex items-center gap-3"><span className="w-2 h-2 bg-accent" /> {x}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* LOCATION MEMORY */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-6">
            <div className="eyebrow text-accent mb-4">03 · Know the Stop Before You Roll In</div>
            <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">Location Memory.</h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Save the gate code, the turn you can't make, the shift supervisor's name — then let HaulGuard warn you the next time that address shows up on a load.
            </p>
            <div className="mt-6 relative bg-accent/10 border-l-4 border-accent p-4">
              <div className="eyebrow text-accent mb-1">HaulGuard location alert</div>
              <div className="font-display font-bold text-lg">"Use Gate 3. Nine-car trucks cannot clear the turn at the front entrance."</div>
              <div className="mt-1 text-xs text-muted-foreground font-mono-tech">Manheim Auction PA · 1190 Lancaster Rd</div>
            </div>
          </div>
          <div className="lg:col-span-6 corner-bracket relative">
            <img src={LOT} alt="Dealership lot" loading="lazy" decoding="async" className="w-full h-96 object-cover"/>
          </div>
        </div>
      </section>

      {/* BUSINESS AROUND THE HAUL */}
      <section className="py-20 lg:py-24 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className="eyebrow text-accent mb-3">04 · Business Around the Haul</div>
          <h2 className="font-display font-black text-3xl lg:text-4xl tracking-tighter mb-10">The record first. The books, right after.</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { t: "Loaded mileage", d: "Logged against each load." },
              { t: "Fuel & tolls", d: "Fast mobile recorder (Phase 2)." },
              { t: "Revenue per vehicle", d: "Compare lanes and brokers (Phase 2)." },
            ].map((c,i) => (
              <div key={i} className="bg-white p-6 border border-border/60">
                <Truck size={28} weight="duotone" className="text-primary"/>
                <div className="mt-3 font-display font-bold text-xl">{c.t}</div>
                <div className="text-sm text-muted-foreground mt-1">{c.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Pricing onStart={() => nav("/register")}/>

      {/* FINAL CTA */}
      <section id="final-cta" className="py-20 lg:py-28 bg-primary text-primary-foreground relative">
        <div className="max-w-5xl mx-auto px-4 lg:px-6 text-center">
          <div className="eyebrow text-accent mb-4">Built for owner-operators</div>
          <h2 className="font-display font-black text-4xl lg:text-6xl tracking-tighter">One hand. One phone.<br/>Nine cars <span className="text-accent">accounted for</span>.</h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <button onClick={() => nav("/register")} data-testid="cta-footer-primary" className="btn-amber">Start your first vehicle record</button>
            <Link to="/login" data-testid="cta-footer-login" className="btn-ghost-dark">Sign in</Link>
          </div>
        </div>
        <div className="amber-stripe h-1 relative mt-16"/>
      </section>

      <footer className="py-8 pb-24 md:pb-8 bg-primary text-white/60 text-center text-xs">
        © {new Date().getFullYear()} HaulGuard · Proof that travels with every VIN.
      </footer>
      <StickyTrial/>
    </div>
  );
}

function StickyTrial() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const end = document.getElementById("final-cta");
    let endVisible = false;
    const update = () => setShow(window.scrollY > window.innerHeight * 0.8 && !endVisible);
    const io = new IntersectionObserver(([e]) => { endVisible = e.isIntersecting; update(); });
    if (end) { io.observe(end); }
    window.addEventListener("scroll", update, { passive: true });
    return () => { io.disconnect(); window.removeEventListener("scroll", update); };
  }, []);
  return <div data-testid="sticky-trial" aria-hidden={!show}
    className={`md:hidden fixed inset-x-0 bottom-0 z-40 bg-primary border-t-4 border-accent px-4 pt-3 transition-transform duration-300 ${show ? "translate-y-0" : "translate-y-full"}`}
    style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}>
    <div className="flex items-center gap-3">
      <div className="flex-1 min-w-0 text-white">
        <div className="font-display font-bold leading-tight">Try HaulGuard free</div>
        <div className="text-[11px] text-white/70">14 days · no credit card needed</div>
      </div>
      <Link to="/register" tabIndex={show ? 0 : -1} data-testid="sticky-trial-cta" className="btn-amber shrink-0">Start free trial <ArrowRight size={16} weight="bold"/></Link>
    </div>
  </div>;
}
